import { StackScreenProps } from "@react-navigation/stack";
import { CreatePaymentMethodRootParamList } from "../../../../types";
import {
  CashRounding,
  PaymentMethodFormValues,
  RoundingAlgorithm,
} from "../../components/PaymentMethodForm/types";
import { useFormContext } from "react-hook-form";
import {
  Card,
  FlatActionList,
  Text,
  Page,
  Stack,
  Divider,
  Button,
  useFlashMessages,
  useWithAppState,
} from "@towersystems/roam-common-native";
import { useTranslation } from "../../../../utilities/i18n";
import { PayTypeIdToName } from "../../../../components/PayTypeIdToName";
import { PaymentMethodForm } from "../../components/PaymentMethodForm";
import { Colors } from "react-native-ui-lib";
import React from "react";
import { useMutation } from "@apollo/client";

import {
  Deleted,
  PaymentMethod,
} from "@towersystems/roam-common/lib/generated-types";
import { useCreateEditPaymentMethodContextProvider } from "../CreatePaymentMethodRootScreen/hooks";
import {
  MUTATION_ADD_PAYMENT_METHOD_TO_TERMINAL,
  MUTATION_DELETE_PAYMENT_METHOD,
} from "../../../../utilities/terminal/constants";
import Terminal from "../../../../utilities/data/model/Terminal";
import { RetailerPayTypeIdToName } from "../../../../components/RetailerPayTypeIdToName";
import {
  CREATE_PAYMENT_METHOD_MUTATION,
  MUTATION_UPDATE_PAYMENT_METHOD,
} from "../../common/constants";
import { useActiveTerminal } from "../../../../utilities/app-state/hooks";

const CASH_PAY_TYPE_ID = "1";

export type CreatePaymentMethodScreenProps = StackScreenProps<
  CreatePaymentMethodRootParamList,
  "CreatePaymentMethodScreen"
>;

export const CreatePaymentMethodScreen = ({
  navigation,
}: CreatePaymentMethodScreenProps) => {
  const maybeParentContext = useCreateEditPaymentMethodContextProvider();
  const flashMessagesContext = useFlashMessages();

  const {
    appState: { terminalId },
  } = useWithAppState<{
    terminalId: string | null;
  }>();

  const [mutationCreatePaymentMethod] = useMutation<{
    createPaymentMethod: PaymentMethod;
  }>(CREATE_PAYMENT_METHOD_MUTATION);

  const [mutationUpdatePaymentMethod] = useMutation<{
    updatePaymentMethod: PaymentMethod;
  }>(MUTATION_UPDATE_PAYMENT_METHOD);

  const [mutationAddTerminal] = useMutation<{
    addPaymentMethodToTerminal: Terminal;
  }>(MUTATION_ADD_PAYMENT_METHOD_TO_TERMINAL);

  const [mutationDeletePaymentMethod] = useMutation<{
    deletePaymentMethod: Deleted;
  }>(MUTATION_DELETE_PAYMENT_METHOD);

  const { t } = useTranslation("settings");

  const { watch, setError, handleSubmit, formState } =
    useFormContext<PaymentMethodFormValues>();

  const { isDirty, isLoading, isSubmitting, isValid, isSubmitSuccessful } =
    formState;

  const watchedPaymentMethodType = watch("payTypeId");
  const watchedId = watch("id");
  const watchedRetailerPayTypeId = watch("retailerPayTypeId");
  const watchedRoundingAlgorithm = watch("rounding.roundingAlgorithm");
  const watchedCashRounding = watch("rounding.cashRounding");

  React.useEffect(() => {
    if (watchedId) {
      navigation.setOptions({
        headerRight: () => (
          <Button
            label={t("button.delete_payment_method")}
            isDestructive
            onPress={() => {
              handleOnDelete(watchedId);
            }}
            size="compact"
          ></Button>
        ),
      });
    }
  }, [watchedId]);

  const handleOnDelete = React.useCallback(async (id: string) => {
    return mutationDeletePaymentMethod({
      variables: {
        id,
      },
    })
      .then((result) => {
        if (result.data?.deletePaymentMethod?.result) {
          if (maybeParentContext?.onAfterDeletePaymentMethod) {
            maybeParentContext.onAfterDeletePaymentMethod(navigation);
          }
        }
      })
      .catch((err) => {
        const msg = err?.message ?? t("common:error");
        flashMessagesContext.showMessage({
          message: msg,
          severity: "error",
        });
        setError("root", {
          type: "serverError",
          message: msg,
        });
      });
  }, []);

  const handleOnSubmit = React.useCallback(
    async (values: PaymentMethodFormValues) => {
      let handler = undefined;

      switch (values.payTypeId) {
        case CASH_PAY_TYPE_ID:
          handler = {
            code: "cash",
            arguments: [
              {
                name: "cashRounding",
                value:
                  values.rounding?.cashRounding ?? CashRounding.ROUND_TO_05,
              },
              {
                name: "roundingAlgorithm",
                value:
                  values.rounding?.roundingAlgorithm ??
                  RoundingAlgorithm.ROUND_MIDPOINT_DOWN,
              },
            ],
          };
          break;
      }

      console.log("handler", values.payTypeId, handler);

      if (values.id) {
        return mutationUpdatePaymentMethod({
          variables: {
            input: {
              id: values.id,
              name: values.name,
              enabled: values.enabled ?? true,
              retailerPayTypeId: values.retailerPayTypeId ?? null,
              handler,
            },
          },
        })
          .then((result) => {
            if (result.data?.updatePaymentMethod) {
              if (maybeParentContext?.onAfterSavePaymentMethod) {
                maybeParentContext.onAfterSavePaymentMethod(
                  result.data.updatePaymentMethod,
                  navigation
                );
              }
              return result;
            }
            throw new Error("Failed to update payment method");
          })
          .catch((err) => {
            const msg = err?.message ?? t("common:error");
            flashMessagesContext.showMessage({
              message: msg,
              severity: "error",
            });
            setError("root", {
              type: "serverError",
              message: msg,
            });
          });
      } else {
        return mutationCreatePaymentMethod({
          variables: {
            input: {
              name: values.name,
              payTypeId: values.payTypeId,
              enabled: values.enabled ?? true,
              retailerPayTypeId: values.retailerPayTypeId ?? undefined,
              handler,
            },
          },
        })
          .then((result) => {
            if (result.data?.createPaymentMethod) {
              if (maybeParentContext?.onAfterSavePaymentMethod) {
                maybeParentContext.onAfterSavePaymentMethod(
                  result.data.createPaymentMethod,
                  navigation
                );
              }

              return result;
            }
            throw new Error("Failed to create payment method");
          })
          .then((result) => {
            if (values.assignToTerminal && terminalId) {
              return mutationAddTerminal({
                variables: {
                  terminalId: terminalId,
                  paymentMethodId: result.data?.createPaymentMethod?.id,
                },
              }).then((result2) => {
                if (result2.data?.addPaymentMethodToTerminal) {
                  if (maybeParentContext?.onAfterAddPaymentMethodTerminal) {
                    maybeParentContext.onAfterAddPaymentMethodTerminal(
                      //@ts-ignore
                      result.data?.createPaymentMethod,
                      result2.data?.addPaymentMethodToTerminal,
                      navigation
                    );
                  }
                }
              });
            }
          })
          .catch((err) => {
            const msg = err?.message ?? "Something went wrong";
            flashMessagesContext.showMessage({
              message: msg,
              severity: "error",
            });
            setError("root", {
              type: "serverError",
              message: msg,
            });
          });
      }
    },
    []
  );

  const selectPayTypeMarkup = (
    <Card>
      <FlatActionList
        items={[
          {
            key: "cash",
            title: t("form.payment_method_pay_type.label"),
            subTitle: t("form.payment_method_pay_type.description"),
            onPress: () => navigation.navigate("SelectPaymentTypeScreen"),
            disabled: Boolean(watchedId),
            componentRight: () => (
              <Text color="action" fontWeight="bold">
                {watchedPaymentMethodType ? (
                  <PayTypeIdToName payTypeId={watchedPaymentMethodType} />
                ) : (
                  t("form.payment_method_pay_type.placeholder")
                )}
              </Text>
            ),
          },
        ]}
      />
    </Card>
  );

  const isCash = watchedPaymentMethodType === CASH_PAY_TYPE_ID;

  const cashMarkup = isCash ? (
    <Card title="Rounding">
      <Stack vertical>
        <FlatActionList
          items={[
            {
              key: "cash_rounding",
              title: t("form.cash_rounding.label"),
              subTitle: t("form.cash_rounding.description"),
              onPress: () => navigation.navigate("SelectCashRoundingScreen"),
              componentRight: () =>
                watchedCashRounding ? (
                  <Text {...textButtonProps}>
                    {t(`form.cash_rounding.${watchedCashRounding}.label`)}
                  </Text>
                ) : (
                  <Text {...textButtonProps}>
                    {t("form.cash_rounding.placeholder")}
                  </Text>
                ),
            },
            {
              key: "cash_rounding_algorithm",
              title: t("form.cash_rounding_algorithm.label"),
              subTitle: t("form.cash_rounding_algorithm.description"),
              onPress: () =>
                navigation.navigate("SelectRoundingAlgorithmScreen"),
              componentRight: () =>
                watchedRoundingAlgorithm ? (
                  <Text {...textButtonProps}>
                    {watchedRoundingAlgorithm
                      ? t(
                          `form.cash_rounding_algorithm.${watchedRoundingAlgorithm}.label`
                        )
                      : t("form.cash_rounding_algorithm.placeholder")}
                  </Text>
                ) : (
                  <Text {...textButtonProps}>
                    {t("form.cash_rounding_algorithm.placeholder")}
                  </Text>
                ),
            },
          ]}
        />
      </Stack>
    </Card>
  ) : undefined;

  const content = watchedPaymentMethodType ? (
    <Card title={t("card.payment_method_details.title")}>
      <Stack vertical>
        <PaymentMethodForm.EnabledFormInput />
        {!watchedId && terminalId && (
          <>
            <Divider marginVertical={0} />
            <PaymentMethodForm.AssignToTerminalInputField />
          </>
        )}
        <Divider marginVertical={0} />
        <PaymentMethodForm.NameInputField />
        <Divider marginVertical={0} />
        <FlatActionList
          items={[
            {
              key: "payment_retailer_paytype",
              title: t("form.payment_retailer_paytype.label"),
              subTitle: t("form.payment_retailer_paytype.description"),
              onPress: () =>
                navigation.navigate("SelectRetailerPayTypeModalScreen"),
              componentRight: () =>
                watchedRetailerPayTypeId ? (
                  <RetailerPayTypeIdToName
                    retailerPayTypeId={watchedRetailerPayTypeId}
                    {...textButtonProps}
                  />
                ) : (
                  <Text {...textButtonProps}>
                    {t("form.payment_retailer_paytype.placeholder")}
                  </Text>
                ),
            },
          ]}
        />
      </Stack>
    </Card>
  ) : undefined;

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.save_payment_method")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page
      title={t(
        watchedId
          ? "update_payment_method_title"
          : "create_payment_method_title"
      )}
      secondaryTitle={t(
        watchedId
          ? "update_payment_method_description"
          : "create_payment_method_description"
      )}
      footer={footerMarkup}
    >
      <Stack vertical>
        {selectPayTypeMarkup}
        {content ? <Divider marginVertical={0} /> : undefined}
        {content}
        {cashMarkup}
      </Stack>
    </Page>
  );
};

const textButtonProps = {
  fontWeight: "bold",
  color: "action",
  variant: "body",
} as any;
